import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { TabList, Tab } from '@fluentui/react-components';
import { useJsData } from '@bingads-webui-cc-react/js-data-rest';
import { StockImagePanelContent, WebsiteImagePanelContent, InvestImagePanelContent, isValidXandrAccount } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { useSourceImages } from './hooks/use-source-images';
import { AdsStudioAddAssetPanel } from './add-asset-panel';

export const MixedImagePanel = (props) => {
  const {
    i18n,
    aid, cid, oDataConfig,
    dataService, logger, notificationApi, navigation, open, perfMarker,
    permissions,
  } = props;

  const isXandrIntEnabled = useMemo(() => permissions.dynamic.XandrIntegrationEnabled, [permissions]);
  // eslint-disable-next-line no-restricted-globals
  const { data: account } = useJsData(jsData => (isXandrIntEnabled ? jsData.Account.find(aid) : Promise.resolve()), [aid, isXandrIntEnabled]);
  const isXandrIntegrationEnabled = useMemo(() => (isXandrIntEnabled ? isValidXandrAccount(account) : false), [account, isXandrIntEnabled]);

  const [selectedTab, setSelectedTab] = useState('stock');
  const {
    setSelectedImages, onStockClickSelect, onInvestClickSelect, onWebsiteClickSelect, showBlocker, selectedImages,
  } = useSourceImages({
    dataService, logger, navigation, perfMarker,
  });

  const dismiss = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const onClickSelect = useCallback((handleJobErrors) => {
    if (selectedTab === 'stock') {
      onStockClickSelect(handleJobErrors);
    } else if (selectedTab === 'invest') {
      onInvestClickSelect(handleJobErrors);
    } else {
      onWebsiteClickSelect(handleJobErrors);
    }
  }, [onStockClickSelect, onInvestClickSelect, onWebsiteClickSelect, selectedTab]);

  const onTabSelect = useCallback((event, data) => {
    setSelectedImages([]);
    setSelectedTab(data.value);
  }, [setSelectedImages]);

  return (
    <AdsStudioAddAssetPanel
      aid={aid}
      i18n={i18n}
      dismiss={dismiss}
      isOpen={open}
      showBlocker={showBlocker}
      selectedAsset={selectedImages}
      headerText={i18n.getString(_TL_('Add images'))}
      logger={logger}
      notificationApi={notificationApi}
      onClickSelect={onClickSelect}
    >
      <TabList selectedValue={selectedTab} onTabSelect={onTabSelect}>
        <Tab value="stock">{i18n.getString(_TL_('Stock images'))}</Tab>
        <Tab value="website">{i18n.getString(_TL_('Website images'))}</Tab>
        {isXandrIntegrationEnabled && <Tab value="invest">{i18n.getString(_TL_('Microsoft Invest'))}</Tab>}
      </TabList>
      {selectedTab === 'stock' && <StockImagePanelContent i18n={i18n} aid={aid} cid={cid} oDataConfig={oDataConfig} setSelectedImages={setSelectedImages} />}
      {selectedTab === 'website' && <WebsiteImagePanelContent i18n={props.i18n} aid={aid} cid={cid} oDataConfig={oDataConfig} setSelectedImages={setSelectedImages} />}
      {selectedTab === 'invest' && isXandrIntegrationEnabled && <InvestImagePanelContent i18n={props.i18n} aid={aid} cid={cid} oDataConfig={oDataConfig} setSelectedImages={setSelectedImages} />}
    </AdsStudioAddAssetPanel>
  );
};

MixedImagePanel.propTypes = {
  i18n: PropTypes.shape({}).isRequired,
  navigation: PropTypes.shape({ gotoImagesPage: PropTypes.func }).isRequired,
  dataService: PropTypes.shape({
    uploadImage: PropTypes.func,
    uploadStockImage: PropTypes.func,
    uploadImageByUrl: PropTypes.func,
    uploadInvestImage: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  aid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  notificationApi: PropTypes.shape({
    showWarning: PropTypes.func,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      XandrIntegrationEnabled: PropTypes.bool,
    }),
  }),
};

MixedImagePanel.defaultProps = {
  permissions: { dynamic: { XandrIntegrationEnabled: false } },
};
